import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  UploadCloud,
  Image as ImageIcon,
  Briefcase,
  Tag,
  Shirt,
  CheckCircle,
  Palette,
  Sparkles,
  Phone,
  MapPin,
  Globe,
  X,
  Layers,
} from 'lucide-react';

/**
 * Inline (controlled) fabric-template setup fields, rendered inside the wizard's
 * final "Content" step when a fabric template is selected.
 *
 * All values are owned by the parent (wizard) state; this component only renders
 * and calls back. Validation markers show when `showErrors` is true (e.g. after a
 * generate attempt with missing required fields).
 *
 * Mandatory: fabric name, company logo, company name, at least one garment design
 * (when recommendations exist). Optional: shade card.
 */
const TAB_LABEL_COLORS = {
  women: 'text-pink-700',
  men: 'text-blue-700',
  kids_boy: 'text-sky-700',
  kids_girl: 'text-violet-700',
};

function SectionCard({ step, title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-indigo-200 bg-white p-3 sm:p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-bold text-white">
          {step}
        </span>
        <Icon size={16} className="text-indigo-600" />
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function FabricTemplateSetupFields({
  template,
  fabricName = '',
  onFabricNameChange,
  companyName = '',
  onCompanyNameChange,
  logoUrl = '',
  logoUploading = false,
  onLogoFileSelected,
  onRemoveLogo,
  shadeCards = [],
  shadeCardUploading = false,
  onShadeCardFileSelected,
  onRemoveShadeCard,
  garmentGroups = [],
  selectedGarments = [],
  onToggleGarment,
  maxGarments = 3,
  phone = '',
  onPhoneChange,
  address = '',
  onAddressChange,
  website = '',
  onWebsiteChange,
  showErrors = false,
  fabricProperties = null,
}) {
  const logoInputRef = useRef(null);
  const shadeCardInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(garmentGroups[0]?.key || '');

  // Keep the active tab valid as groups change (e.g. category switch upstream).
  useEffect(() => {
    if (!garmentGroups.some((g) => g.key === activeTab)) {
      setActiveTab(garmentGroups[0]?.key || '');
    }
  }, [garmentGroups, activeTab]);

  const hasGarmentOptions =
    Array.isArray(garmentGroups) && garmentGroups.some((g) => (g?.items || []).length > 0);
  const activeGroup = garmentGroups.find((g) => g.key === activeTab) || garmentGroups[0] || null;
  const isGarmentSelected = (name, audienceKey) =>
    selectedGarments.some((g) => g.garment === name && g.audienceKey === audienceKey);
  const selectedCountForGroup = (group) =>
    selectedGarments.filter((g) => g.audienceKey === group?.key).length;

  const errors = useMemo(() => {
    const next = {};
    if (!String(fabricName).trim()) next.fabricName = 'Fabric name is required';
    if (!String(companyName).trim()) next.companyName = 'Company name is required';
    if (!logoUrl) next.logo = 'Company logo is required';
    if (hasGarmentOptions && selectedGarments.length === 0) {
      next.garment = 'Choose at least one garment design';
    }
    return next;
  }, [fabricName, companyName, logoUrl, selectedGarments, hasGarmentOptions]);

  const showError = (key) => showErrors && errors[key];

  return (
    <div className="space-y-3">
      {template?.name && (
        <p className="text-xs text-slate-500 truncate">Using “{template.name}”</p>
      )}

      <SectionCard step={1} title="Edit fabric details" icon={Layers}>
        <p className="text-xs text-slate-500 -mt-1">
          Edits apply to this catalog only. Empty or removed fields are not shown on the template, and
          the original fabric is not updated.
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <Tag size={15} className="text-indigo-600" />
            Fabric name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={fabricName}
            onChange={(e) => onFabricNameChange?.(e.target.value)}
            placeholder="e.g. Premium Cotton Poplin"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
              showError('fabricName') ? 'border-red-400' : 'border-slate-300'
            }`}
          />
          {showError('fabricName') && <p className="text-xs text-red-500 mt-1">{errors.fabricName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <Palette size={15} className="text-indigo-600" />
            Shade card <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          {shadeCards && shadeCards.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {shadeCards.map((card, index) => (
                <div key={index} className="relative">
                  <img
                    src={card.preview || card.url}
                    alt={`Shade card ${index + 1}`}
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  {onRemoveShadeCard && (
                    <button
                      type="button"
                      onClick={() => onRemoveShadeCard(index)}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                      aria-label="Remove shade card"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => shadeCardInputRef.current?.click()}
            disabled={shadeCardUploading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
          >
            {shadeCardUploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {shadeCardUploading ? 'Uploading…' : shadeCards?.length > 0 ? 'Add another shade card' : 'Upload shade card'}
          </button>
          <input
            ref={shadeCardInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onShadeCardFileSelected?.(file);
              e.target.value = '';
            }}
          />
        </div>

        {fabricProperties}
      </SectionCard>

      <SectionCard step={2} title="Edit company details" icon={Briefcase}>
        <p className="text-xs text-slate-500 -mt-1">
          Leave optional fields blank to keep them off the catalog. Company details are not saved back
          to the template.
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <Briefcase size={15} className="text-indigo-600" />
            Company name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => onCompanyNameChange?.(e.target.value)}
            placeholder="Your company / brand name"
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
              showError('companyName') ? 'border-red-400' : 'border-slate-300'
            }`}
          />
          {showError('companyName') && <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <ImageIcon size={15} className="text-indigo-600" />
            Company logo <span className="text-red-500">*</span>
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-3 bg-white transition ${
              showError('logo') ? 'border-red-400' : 'border-slate-300 hover:border-indigo-400'
            }`}
          >
            {logoUploading ? (
              <div className="flex items-center gap-2 text-sm text-indigo-600">
                <Loader2 size={16} className="animate-spin" />
                Uploading logo...
              </div>
            ) : logoUrl ? (
              <div className="flex items-center gap-3">
                <img
                  src={logoUrl}
                  alt="Company logo"
                  className="w-14 h-14 object-contain rounded-lg border border-slate-200 bg-white p-1"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">Logo uploaded</p>
                  <p className="text-xs text-slate-500">Shown on the fabric template</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Replace
                  </button>
                  {onRemoveLogo && (
                    <button
                      type="button"
                      onClick={() => onRemoveLogo()}
                      className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-14 h-14 rounded-lg border border-slate-200 bg-white flex items-center justify-center shrink-0">
                  <ImageIcon size={22} className="text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-700">Upload your company logo</p>
                  <p className="text-xs text-slate-500 mt-0.5">PNG or JPG recommended</p>
                </div>
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  <UploadCloud size={16} />
                  Upload logo
                </button>
              </div>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onLogoFileSelected?.(file);
                e.target.value = '';
              }}
            />
          </div>
          {showError('logo') && <p className="text-xs text-red-500 mt-1">{errors.logo}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <Briefcase size={15} className="text-indigo-600" />
            Company contact <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Leave blank to keep it off the template — empty fields are never shown.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone size={15} className="text-slate-400 shrink-0" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => onPhoneChange?.(e.target.value)}
                placeholder="Phone number"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={15} className="text-slate-400 shrink-0 mt-2.5" />
              <textarea
                value={address}
                onChange={(e) => onAddressChange?.(e.target.value)}
                placeholder="Address"
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Globe size={15} className="text-slate-400 shrink-0" />
              <input
                type="text"
                value={website}
                onChange={(e) => onWebsiteChange?.(e.target.value)}
                placeholder="Website"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard step={3} title="Edit garment details" icon={Shirt}>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
            <Shirt size={15} className="text-indigo-600" />
            Garment design {hasGarmentOptions && <span className="text-red-500">*</span>}
            {hasGarmentOptions && (
              <span className="ml-auto text-xs font-medium text-slate-400">
                {selectedGarments.length}/{maxGarments} selected
              </span>
            )}
          </label>
          {hasGarmentOptions ? (
            <>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
                <Sparkles size={13} className="text-indigo-500" />
                Recommended from your fabric analysis — pick up to {maxGarments} across the tabs to feature in the template.
              </p>

              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5">
                {garmentGroups.map((group) => {
                  const isActive = activeGroup?.key === group.key;
                  const count = selectedCountForGroup(group);
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => setActiveTab(group.key)}
                      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        isActive
                          ? 'border-indigo-500 bg-indigo-50 ' + (TAB_LABEL_COLORS[group.key] || 'text-indigo-700')
                          : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                      }`}
                    >
                      {group.label}
                      <span
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 ${
                          count > 0 ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
                        }`}
                        title={count > 0 ? `${count} selected` : `${group.items.length} options`}
                      >
                        {count > 0 ? count : group.items.length}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2 mt-2">
                {(activeGroup?.items || []).map((opt, idx) => {
                  const name = (opt?.garment || '').trim();
                  if (!name) return null;
                  const active = isGarmentSelected(name, activeGroup?.key);
                  const capReached = !active && selectedGarments.length >= maxGarments;
                  return (
                    <button
                      key={`${activeGroup?.key}-${name}-${idx}`}
                      type="button"
                      onClick={() => onToggleGarment?.(name, activeGroup?.key, activeGroup?.label)}
                      disabled={capReached}
                      aria-pressed={active}
                      className={`w-full text-left rounded-lg border px-3 py-2 transition ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                          : capReached
                            ? 'border-slate-200 bg-white opacity-50 cursor-not-allowed'
                            : 'border-slate-200 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <CheckCircle
                          size={16}
                          className={`mt-0.5 shrink-0 ${active ? 'text-indigo-600' : 'text-slate-300'}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900">{name}</p>
                          {opt?.reason && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{opt.reason}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedGarments.length >= maxGarments && (
                <p className="text-xs text-slate-400 mt-1.5">
                  You can feature up to {maxGarments} garment designs. Deselect one to change your choices.
                </p>
              )}
              {showError('garment') && <p className="text-xs text-red-500 mt-1">{errors.garment}</p>}
            </>
          ) : (
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Palette size={13} className="text-slate-400" />
              No garment recommendations available — the template’s default style will be used.
            </p>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

